/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { Handler, HasOnSuccess, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { Cart, CartResult, UpdateCartApiParameter } from "@insite/client-framework/Services/CartService";
import { createShipTo, updateBillTo, updateShipTo } from "@insite/client-framework/Services/CustomersService";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import {
    chain,
    DispatchBeginUpdateCart,
    LoadCart,
    UpdateBillTo,
} from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import { BaseAddressModel, BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";

type HandlerType = Handler<
    HasOnSuccess,
    {
        shipTo?: ShipToModel;
        updatedBillTo?: BillToModel;
        updatedShipTo?: ShipToModel;
        cart: Cart;
        apiParameter: UpdateCartApiParameter;
        apiResult: CartResult;
    }
>;

function areAddressesTheSame(address1: BaseAddressModel, address2: BaseAddressModel) {
    return (
        address1.firstName === address2.firstName &&
        address1.lastName === address2.lastName &&
        address1.attention === address2.attention &&
        address1.companyName === address2.companyName &&
        address1.address1 === address2.address1 &&
        address1.address2 === address2.address2 &&
        address1.address3 === address2.address3 &&
        address1.address4 === address2.address4 &&
        address1.country?.id === address2.country?.id &&
        address1.city === address2.city &&
        address1.state?.id === address2.state?.id &&
        address1.postalCode === address2.postalCode &&
        address1.phone === address2.phone &&
        address1.email === address2.email &&
        address1.fax === address2.fax
    );
}

export const xDispatchBeginUpdateCart: any = (props: any) => {
    props.dispatch({
        type: "Pages/CheckoutShipping/BeginUpdateCart",
    });
    (props.parameter as any).onException = () => {
        props.dispatch({
            type: "Pages/CheckoutShipping/CompleteUpdateCart",
        });
    };
};

export const xLoadCart: any = (props: any) => {
    const fullExpand = props.parameter.fullExpand;
    const state = props.getState();
    const { cartId } = state.pages.checkoutShipping;
    if (cartId) {
        props.dispatch(loadCart({ cartId, fullExpand } as any));
    } else {
        props.dispatch(loadCurrentCart({ fullExpand, getPromotions: true } as any));
    }
};

export const xUpdateBillTo: HandlerType = async props => {
    const { dispatch, getState, cart } = props;
    const state = getState();
    const pageType: any = getCurrentPage(state).type;

    const {
        pages: {
            checkoutShipping: { billingAddressFormState, shippingAddressFormState },
        },
    } = state;

    if (!billingAddressFormState) {
        return;
    }
    
    if (!shippingAddressFormState) {
        return;
    }

    const billTo = getBillToState(state, cart.billToId).value;
    if (!billTo) {
        throw new Error("There was no shipTo or billTo loaded for the current cart");
    }

    const { address: localModifiedBillTo } = billingAddressFormState;
    const { address: localModifiedShipTo } = shippingAddressFormState

    if (!areAddressesTheSame(billTo, localModifiedBillTo) || props.updatedShipTo) {
        if (pageType === "CheckoutShippingPage" && localModifiedShipTo.label === "Ship to One-Time Address") {
            // xng change: EIS-3506: sending "BillToAddressUpdateDisable" property when one-time-shipto
            props.updatedBillTo = await updateBillTo({
                billTo: {
                    ...localModifiedBillTo,
                    properties: {
                        ...localModifiedBillTo.properties,
                        BillToAddressUpdateDisable: "true",
                    },
                },
                isEdit: true,
            });
    
            dispatch(loadBillTo({ billToId: billTo.id }));
            if (cart.shipToId === billTo.id) {
                dispatch(loadShipTo({ shipToId: cart.shipToId, billToId: billTo.id }));
            }
    
            props.apiParameter.cart.billToId = props.updatedBillTo.id;
        } else {
            props.updatedBillTo = await updateBillTo({
                billTo: localModifiedBillTo,
                isEdit: true,
            });
    
            dispatch(loadBillTo({ billToId: billTo.id }));
            if (cart.shipToId === billTo.id) {
                dispatch(loadShipTo({ shipToId: cart.shipToId, billToId: billTo.id }));
            }
    
            props.apiParameter.cart.billToId = props.updatedBillTo.id;
        }
    }
};

replaceInChain(chain, DispatchBeginUpdateCart, xDispatchBeginUpdateCart);
replaceInChain(chain, LoadCart, xLoadCart);
replaceInChain(chain, UpdateBillTo, xUpdateBillTo);
